/**
 * Ponto de entrada. Este arquivo e o CONTRATO DE INTEGRACAO do projeto:
 * define quem e construido, em que ordem, e em que ordem roda por frame.
 *
 * Se voce e um sistema do jogo, NAO edite este arquivo para se encaixar -
 * implemente a interface que ele ja espera de voce (ver ARCHITECTURE.md).
 */

import { EventBus, EVENTS } from './core/EventBus.js';
import { Settings } from './core/Settings.js';
import { Input } from './core/Input.js';
import { Engine } from './core/Engine.js';

import { RendererSystem } from './render/Renderer.js';
import { MaterialLibrary } from './render/Materials.js';
import { SkySystem } from './render/Sky.js';
import { LightingSystem } from './render/Lighting.js';
import { PostFX } from './render/PostFX.js';

import { CollisionWorld } from './world/Collision.js';
import { Level } from './world/Level.js';

import { Player } from './player/Player.js';
import { PlayerController } from './player/PlayerController.js';
import { CameraRig } from './player/CameraRig.js';

import { TargetRegistry } from './combat/Targets.js';
import { Ballistics } from './combat/Ballistics.js';

import { WeaponSystem } from './weapons/WeaponSystem.js';
import { ViewModel } from './weapons/ViewModel.js';

import { BotManager } from './ai/BotManager.js';

import { FxSystem } from './fx/FxSystem.js';
import { AudioSystem } from './audio/AudioSystem.js';
import { HUD } from './ui/HUD.js';
import { Menu } from './ui/Menu.js';
import { GameState } from './game/GameState.js';

// --- multijogador, conta e celular -----------------------------------------
import { Session } from './services/Session.js';
import { NetClient } from './net/NetClient.js';
import { RemotePlayers } from './net/RemotePlayers.js';
import { NetSync } from './net/NetSync.js';
import { MatchState } from './game/MatchState.js';
import { AuthScreen } from './ui/AuthScreen.js';
import { Lobby } from './ui/Lobby.js';
import { Scoreboard } from './ui/Scoreboard.js';
import { RankingScreen } from './ui/RankingScreen.js';
import { TouchControls } from './ui/TouchControls.js';

import { installDebugApi } from './debug/DebugApi.js';

const BOOT_STEPS = [
  'renderer', 'materiais', 'ceu', 'iluminacao', 'colisao', 'cenario',
  'jogador', 'armas', 'combate', 'inimigos', 'efeitos', 'audio', 'interface',
  'conta', 'rede', 'toque', 'pos-processamento'
];

function showFatal(err) {
  console.error('[boot] falha fatal:', err);
  const root = document.getElementById('ui-root') || document.body;
  const box = document.createElement('div');
  box.style.cssText = `position:fixed;inset:0;z-index:9999;background:#0b0d10;color:#e8eaed;
    font:14px/1.5 ui-monospace,Consolas,monospace;padding:32px;overflow:auto;white-space:pre-wrap`;
  box.textContent = `FALHA AO INICIAR\n\n${err?.stack || err}`;
  root.appendChild(box);
}

function setBootProgress(label, index) {
  const el = document.getElementById('boot-progress');
  if (el) el.textContent = `${label} (${index + 1}/${BOOT_STEPS.length})`;
  const bar = document.getElementById('boot-bar');
  if (bar) bar.style.width = `${((index + 1) / BOOT_STEPS.length) * 100}%`;
}

async function boot() {
  const canvas = document.getElementById('game-canvas');
  const settings = new Settings();
  const events = new EventBus();
  const input = new Input(canvas, settings);
  const engine = new Engine({ canvas, settings, events });

  /**
   * Contexto compartilhado. Todo sistema recebe isto no construtor e le daqui
   * os outros sistemas. Nenhum sistema deve importar outro diretamente para
   * pegar uma instancia - use o ctx.
   */
  const ctx = {
    canvas, settings, events, input, engine, EVENTS,
    scene: engine.scene,
    viewScene: engine.viewScene,
    camera: engine.camera,
    viewCamera: engine.viewCamera,
    renderer: null,
    // sistemas (preenchidos abaixo, nesta ordem)
    materials: null, sky: null, lighting: null, postfx: null,
    collision: null, level: null,
    player: null, controller: null, cameraRig: null,
    targets: null, ballistics: null,
    weapons: null, viewModel: null,
    bots: null, fx: null, audio: null, hud: null, menu: null, game: null,
    // multijogador e conta
    session: null, api: null,
    net: null, remotePlayers: null, netSync: null, match: null,
    auth: null, lobby: null, scoreboard: null, ranking: null,
    // celular
    touch: null
  };

  let step = 0;
  const stage = async (name, fn) => {
    setBootProgress(name, step++);
    // deixa o browser pintar a barra de carregamento entre etapas pesadas
    await new Promise((r) => requestAnimationFrame(() => r()));
    return fn();
  };

  // --- 1. renderizacao base ------------------------------------------------
  await stage('renderer', async () => {
    ctx.renderer = (engine.renderer = new RendererSystem(ctx).create());
  });
  await stage('materiais', async () => { ctx.materials = new MaterialLibrary(ctx); await ctx.materials.init(); });
  await stage('ceu', async () => { ctx.sky = new SkySystem(ctx); await ctx.sky.init(); });
  await stage('iluminacao', async () => { ctx.lighting = new LightingSystem(ctx); await ctx.lighting.init(); });

  // --- 2. mundo ------------------------------------------------------------
  await stage('colisao', async () => { ctx.collision = new CollisionWorld(ctx); await ctx.collision.init(); });
  await stage('cenario', async () => {
    ctx.level = new Level(ctx);
    await ctx.level.init();          // Level DEVE chamar ctx.collision.addStatic(mesh) para tudo solido
    ctx.collision.build();           // BVH construido depois que o cenario existe
  });

  // --- 3. jogador ----------------------------------------------------------
  await stage('jogador', async () => {
    ctx.player = new Player(ctx);
    ctx.controller = new PlayerController(ctx);
    ctx.cameraRig = new CameraRig(ctx);
    await ctx.player.init(); await ctx.controller.init(); await ctx.cameraRig.init();
  });

  // --- 4. combate e armas --------------------------------------------------
  await stage('combate', async () => {
    ctx.targets = new TargetRegistry(ctx);
    ctx.ballistics = new Ballistics(ctx);
    await ctx.targets.init(); await ctx.ballistics.init();
  });
  await stage('armas', async () => {
    ctx.weapons = new WeaponSystem(ctx);
    ctx.viewModel = new ViewModel(ctx);
    await ctx.weapons.init(); await ctx.viewModel.init();
  });

  // --- 5. inimigos ---------------------------------------------------------
  await stage('inimigos', async () => { ctx.bots = new BotManager(ctx); await ctx.bots.init(); });

  // --- 6. apresentacao -----------------------------------------------------
  await stage('efeitos', async () => { ctx.fx = new FxSystem(ctx); await ctx.fx.init(); });
  await stage('audio', async () => { ctx.audio = new AudioSystem(ctx); await ctx.audio.init(); });
  await stage('interface', async () => {
    ctx.hud = new HUD(ctx);
    ctx.menu = new Menu(ctx);
    ctx.game = new GameState(ctx);
    await ctx.hud.init(); await ctx.menu.init(); await ctx.game.init();
  });

  // --- 6b. conta, telas de rede e ranking ---------------------------------
  // Depois da interface de proposito: estas telas reagem a AUTH_CHANGED e a
  // MATCH_*, e o HUD e o Menu precisam ja existir para o estado inicial casar.
  await stage('conta', async () => {
    ctx.session = new Session(ctx);
    ctx.auth = new AuthScreen(ctx);
    ctx.lobby = new Lobby(ctx);
    ctx.scoreboard = new Scoreboard(ctx);
    ctx.ranking = new RankingScreen(ctx);
    await ctx.auth.init(); await ctx.lobby.init();
    await ctx.scoreboard.init(); await ctx.ranking.init();
    // O Session cria o proprio Api; publicar em ctx.api evita que cada tela
    // precise saber que ele mora dentro da sessao.
    await ctx.session.init();
    ctx.api = ctx.session.api;
  });

  // --- 6c. rede -------------------------------------------------------------
  await stage('rede', async () => {
    ctx.net = new NetClient(ctx);
    ctx.remotePlayers = new RemotePlayers(ctx);
    ctx.netSync = new NetSync(ctx);
    ctx.match = new MatchState(ctx);
    await ctx.net.init(); await ctx.remotePlayers.init();
    await ctx.netSync.init(); await ctx.match.init();
  });

  // --- 6d. controles de toque ----------------------------------------------
  // Por ultimo entre os sistemas de entrada: ele so se instala quando o
  // dispositivo tem toque de verdade (ver src/core/Device.js).
  await stage('toque', async () => {
    ctx.touch = new TouchControls(ctx);
    await ctx.touch.init();
  });

  // --- 7. pos-processamento (por ultimo: precisa de cena e camera prontas) --
  await stage('pos-processamento', async () => {
    ctx.postfx = new PostFX(ctx);
    await ctx.postfx.init();
    engine.composer = ctx.postfx.composer;
  });

  // ORDEM DE EXECUCAO POR FRAME. Mexer aqui muda o comportamento do jogo inteiro.
  engine.add(ctx.touch);         // toque -> entrada virtual (ANTES de qualquer leitura)
  engine.add(ctx.net);           // bombeia a rede: o que chegou entra antes de todos
  engine.add(ctx.match);         // estado da partida online
  engine.add(ctx.game);          // estado de jogo, ondas, respawn (campanha)
  engine.add(ctx.controller);    // fixedUpdate: movimento + colisao do jogador
  engine.add(ctx.player);        // vida, regeneracao
  engine.add(ctx.bots);          // fixedUpdate: IA e movimento dos bots
  engine.add(ctx.remotePlayers); // interpola os outros jogadores ANTES do raycast das armas
  engine.add(ctx.weapons);       // disparo, recarga, troca
  engine.add(ctx.cameraRig);     // olhar, bob, recuo, shake  (depois das armas!)
  engine.add(ctx.netSync);       // envia o estado local com camera e movimento ja fechados
  engine.add(ctx.viewModel);     // animacao da arma na tela
  engine.add(ctx.fx);            // particulas, decalques, tracers
  engine.add(ctx.sky);           // ciclo/animacao do ceu
  engine.add(ctx.lighting);      // sombras seguindo a camera
  engine.add(ctx.audio);         // listener e sons posicionais
  engine.add(ctx.hud);           // interface
  engine.add(ctx.scoreboard);    // placar da partida online (Tab e tela final)
  engine.add(ctx.postfx);        // autoexposicao, motion blur temporal
  engine.add({ update: () => input.endFrame() }); // SEMPRE por ultimo

  engine.resize();
  engine.start();

  installDebugApi(ctx);
  document.getElementById('boot-screen')?.classList.add('done');
  ctx.events.emit(EVENTS.GAME_STATE, { state: 'menu' });

  return ctx;
}

boot().catch(showFatal);
